import { create } from 'zustand';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  AuthError,
  AuthErrorCodes
} from 'firebase/auth';
import { doc, setDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { Establishment } from '../types/establishment';

const getAuthErrorMessage = (error: AuthError): string => {
  switch (error.code) {
    case AuthErrorCodes.USER_DELETED:
    case AuthErrorCodes.USER_NOT_FOUND:
      return 'Não existe uma conta com este email.';
    case AuthErrorCodes.INVALID_PASSWORD:
    case AuthErrorCodes.INVALID_EMAIL:
      return 'Email ou senha incorretos.';
    case AuthErrorCodes.TOO_MANY_ATTEMPTS_TRY_LATER:
      return 'Muitas tentativas incorretas. Tente novamente mais tarde.';
    case AuthErrorCodes.EMAIL_EXISTS:
      return 'Este email já está em uso.';
    case AuthErrorCodes.WEAK_PASSWORD:
      return 'A senha deve ter pelo menos 6 caracteres.';
    default:
      return 'Ocorreu um erro ao fazer login. Tente novamente.';
  }
};

interface EstablishmentStore {
  establishment: Establishment | null;
  loading: boolean;
  error: string | null;
  register: (data: any) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateSettings: (settings: any) => Promise<void>;
  checkAuth: () => Promise<void>;
}

export const useEstablishmentStore = create<EstablishmentStore>((set) => ({
  establishment: null,
  loading: true,
  error: null,

  checkAuth: async () => {
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user) {
          try {
            const establishmentDoc = await getDoc(doc(db, 'establishments', user.uid));
            if (establishmentDoc.exists()) {
              const establishmentData = establishmentDoc.data() as Omit<Establishment, 'id'>;
              set({
                establishment: { id: user.uid, ...establishmentData },
                loading: false,
                error: null
              });
            } else {
              set({ establishment: null, loading: false, error: null });
            }
          } catch (error) {
            console.error('Error checking auth:', error);
            set({ establishment: null, loading: false, error: null });
          }
        } else {
          set({ establishment: null, loading: false, error: null });
        }
        resolve();
        unsubscribe();
      });
    });
  },

  register: async (data) => {
    try {
      set({ loading: true, error: null });
      
      const emailQuery = query(
        collection(db, 'establishments'),
        where('email', '==', data.email)
      );
      const emailSnapshot = await getDocs(emailQuery);
      
      if (!emailSnapshot.empty) {
        throw new Error('Este email já está cadastrado');
      }

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );

      const establishmentData: Omit<Establishment, 'id'> = {
        name: data.name,
        email: data.email,
        phone: data.phone,
        address: data.address,
        type: data.type,
        settings: {
          pointsPerVisit: 10,
          rewards: [
            {
              id: '1',
              pointsRequired: 100,
              description: 'Desconto de 10%',
              active: true
            }
          ]
        },
        createdAt: new Date().toISOString()
      };

      await setDoc(doc(db, 'establishments', userCredential.user.uid), establishmentData);

      set({
        establishment: { id: userCredential.user.uid, ...establishmentData },
        loading: false,
        error: null
      });
    } catch (error: any) {
      console.error('Registration error:', error);
      set({ error: getAuthErrorMessage(error), loading: false });
      throw error;
    }
  },

  login: async (email: string, password: string) => {
    try {
      set({ loading: true, error: null });
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      const establishmentDoc = await getDoc(
        doc(db, 'establishments', userCredential.user.uid)
      );

      if (!establishmentDoc.exists()) {
        set({ error: 'Conta não encontrada. Verifique suas credenciais.', loading: false });
        await auth.signOut();
        throw new Error('Establishment not found');
      }

      const establishmentData = establishmentDoc.data() as Omit<Establishment, 'id'>;

      set({
        establishment: { id: userCredential.user.uid, ...establishmentData },
        loading: false,
        error: null
      });
    } catch (error: any) {
      console.error('Login error:', error);
      set({ error: getAuthErrorMessage(error), loading: false });
      throw error;
    }
  },

  logout: async () => {
    try {
      set({ loading: true, error: null });
      await auth.signOut();
      set({ establishment: null, loading: false, error: null });
    } catch (error: any) {
      console.error('Logout error:', error);
      set({ error: getAuthErrorMessage(error), loading: false });
      throw error;
    }
  },

  updateSettings: async (settings) => {
    try {
      set({ loading: true, error: null });
      
      const { establishment } = useEstablishmentStore.getState();
      if (!establishment) throw new Error('Não autorizado');

      await setDoc(
        doc(db, 'establishments', establishment.id),
        { settings },
        { merge: true }
      );

      set(state => ({
        establishment: state.establishment ? {
          ...state.establishment,
          settings
        } : null,
        loading: false,
        error: null
      }));
    } catch (error: any) {
      console.error('Update settings error:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  }
}));

// Initialize auth state check
useEstablishmentStore.getState().checkAuth();